import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { StorageError } from '@supabase/storage-js';
import { NOTICE_PLACEHOLDERS, type NoticeChannel } from '../config/notificationPlaceholders';
import { getSupabaseClient } from './supabase';
import { readAllPages } from './readAllPages';

type ClientOption = { supabase?: SupabaseClient };

export type ProfileDisplayNameMap = Record<string, string>;

export type ProfileRow = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  phone: string | null;
  mobile_phone: string | null;
  mobile_phone_verified_at: string | null;
  secondary_email: string | null;
  sms_opt_in: boolean | null;
  home_location_id: string | null;
  home_region_id: string | null;
  headshot_url: string | null;
};

export type NoticePreferenceRow = {
  notice_code: string;
  delivery_channels: NoticeChannel[];
  reminder_days_before: number | null;
};

export type UserProfileLocation = { location_id: string; name: string };
export type UserProfileRegion = {
  region_id: string;
  name: string;
  is_active: boolean;
  is_manager: boolean;
  grant_all_locations: boolean;
};
export type UserProfileRegionOption = { region_id: string; name: string; is_active: boolean };
export type UserProfileOverview = {
  profile: ProfileRow | null;
  assignedLocations: UserProfileLocation[];
  allLocations: UserProfileLocation[];
  assignedRegions: UserProfileRegion[];
  allRegions: UserProfileRegionOption[];
  noticePrefs: Record<string, NoticeChannel[]>;
};

export type LocationManager = { id: string; name: string; email: string | null };
export type UserOption = { id: string; label: string };
export type UserAuthNameMap = Record<string, string>;

type ServiceLaborUserOptionRpcRow = { id?: string | null; label?: string | null };

function normalizeRpcUserOptions(rows: ServiceLaborUserOptionRpcRow[] | null | undefined): UserOption[] {
  const options = (Array.isArray(rows) ? rows : [])
    .filter((row): row is ServiceLaborUserOptionRpcRow & { id: string } => Boolean(row?.id))
    .map((row) => ({ id: String(row.id), label: row.label?.trim() || 'Unknown user' }));
  options.sort((a, b) => a.label.localeCompare(b.label));
  return options;
}

const PROFILE_SELECT = [
  'id',
  'email',
  'first_name',
  'last_name',
  'full_name',
  'phone',
  'mobile_phone',
  'mobile_phone_verified_at',
  'secondary_email',
  'sms_opt_in',
  'home_location_id',
  'home_region_id',
  'headshot_url',
].join(',');

// Microsoft 365 display names end in " ITA"; staff names show without it (Alex, 2026-10-21). The release clears it
// from stored names; this covers a name that still has it (private.strip_staff_name_suffix is the same rule).
const STAFF_NAME_SUFFIX = /\s+ITA$/;

type ProfileNameRow = { id: string; full_name: string | null; first_name: string | null; last_name: string | null; email: string | null };

export function stripStaffNameSuffix(name: string): string {
  return name.trim().replace(STAFF_NAME_SUFFIX, '');
}

export function formatProfileDisplayName(profile: {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): string {
  const fullName = profile.full_name ? stripStaffNameSuffix(profile.full_name) : '';
  if (fullName) return fullName;

  const name = [profile.first_name, profile.last_name]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (name) return name;

  return profile.email?.trim() || 'Unknown user';
}

export async function fetchProfileDisplayNames(
  profileIds: Array<string | null | undefined>,
  options?: ClientOption,
): Promise<{ data: ProfileDisplayNameMap; error: PostgrestError | null }> {
  const ids = Array.from(new Set(profileIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)));
  if (!ids.length) return { data: {}, error: null };

  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id,full_name,first_name,last_name,email')
    .in('id', ids);
  if (error) return { data: {}, error };

  const map: ProfileDisplayNameMap = {};
  (Array.isArray(data) ? data : []).forEach(row => {
    const id = typeof row?.id === 'string' ? row.id : null;
    if (!id) return;
    map[id] = formatProfileDisplayName(row as any);
  });
  return { data: map, error: null };
}

export async function fetchProfileById(profileId: string, client?: SupabaseClient): Promise<{
  data: ProfileRow | null;
  error: PostgrestError | null;
}> {
  const supabase = client ?? getSupabaseClient();
  const { data, error } = await supabase.from('user_profiles').select(PROFILE_SELECT).eq('id', profileId).maybeSingle();
  return { data: (data as ProfileRow | null) ?? null, error: error ?? null };
}

export async function fetchNoticePreferences(profileId: string, client?: SupabaseClient): Promise<{
  data: NoticePreferenceRow[];
  error: PostgrestError | null;
}> {
  const supabase = client ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('user_notice_preferences')
    .select('notice_code, delivery_channels, reminder_days_before')
    .eq('user_profile_id', profileId);
  return { data: Array.isArray(data) ? (data as NoticePreferenceRow[]) : [], error: error ?? null };
}

export async function updateProfileById(
  profileId: string,
  payload: Partial<ProfileRow>,
  client?: SupabaseClient,
): Promise<PostgrestError | null> {
  const supabase = client ?? getSupabaseClient();
  const { error } = await supabase.from('user_profiles').update(payload).eq('id', profileId).select('id').single();
  return error ?? null;
}

export async function upsertNoticePreferences(
  preferences: Array<{
    user_profile_id: string;
    notice_code: string;
    delivery_channels: NoticeChannel[];
    reminder_days_before?: number | null;
  }>,
  client?: SupabaseClient,
): Promise<PostgrestError | null> {
  const supabase = client ?? getSupabaseClient();
  if (!preferences.length) return null;
  const { error } = await supabase
    .from('user_notice_preferences')
    .upsert(preferences, { onConflict: 'user_profile_id,notice_code' });
  return error ?? null;
}

export async function uploadProfileHeadshot(params: {
  profileId: string;
  file: File;
  client?: SupabaseClient;
}): Promise<{ data: string | null; error: StorageError | PostgrestError | null }> {
  const supabase = params.client ?? getSupabaseClient();
  const BUCKET = 'user-uploads';
  const { data: authData } = await supabase.auth.getUser();
  const authUserId = authData.user?.id ?? null;
  if (!authUserId) {
    return { data: null, error: new Error('Please sign in again before uploading a profile picture.') as any };
  }
  
  // Delete old headshot if it exists
  const { data: profileData } = await supabase
    .from('user_profiles')
    .select('headshot_url')
    .eq('id', params.profileId)
    .maybeSingle();
  
  if (profileData?.headshot_url) {
    // Extract bucket and path from URL
    const urlMatch = profileData.headshot_url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+?)(?:\?|$)/);
    if (urlMatch) {
      const [, oldBucket, oldPath] = urlMatch;
      // Delete from whichever bucket it was in (handles migration from old 'photos' bucket)
      if (oldBucket && oldPath) {
        await supabase.storage.from(oldBucket).remove([oldPath]);
      }
    }
  }
  
  // Upload new headshot to 'user-uploads' bucket
  const timestamp = Date.now();
  const fileExt = params.file.name.split('.').pop() || 'jpg';
  // Storage RLS for user-uploads expects the second folder segment to be auth.uid().
  const path = `headshots/${authUserId}/${timestamp}.${fileExt}`;
  
  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, params.file, {
      cacheControl: '3600',
      upsert: true,
    });
  
  if (uploadError) {
    return { data: null, error: uploadError };
  }
  
  // Get public URL
  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
  const publicUrl = urlData?.publicUrl || null;
  
  if (!publicUrl) {
    return { data: null, error: new Error('Failed to get public URL') as any };
  }
  
  // Update profile with new headshot URL
  const { error: updateError } = await supabase
    .from('user_profiles')
    .update({ headshot_url: publicUrl })
    .eq('id', params.profileId);
  
  if (updateError) {
    return { data: null, error: updateError };
  }
  
  return { data: publicUrl, error: null };
}

export async function fetchLocationManagersByLocation(
  locationIds: string[],
  client?: SupabaseClient,
): Promise<{ managersByLocation: Record<string, LocationManager[]>; error: PostgrestError | null }> {
  if (!locationIds.length) {
    return { managersByLocation: {}, error: null };
  }
  const supabase = client ?? getSupabaseClient();
  const uniqueIds = Array.from(new Set(locationIds.filter(Boolean)));
  if (uniqueIds.length === 0) {
    return { managersByLocation: {}, error: null };
  }
  const { data, error } = await supabase
    .from('user_profile_locations')
    .select('location_id, user_profiles(id, full_name, email, is_archived, is_banned)')
    .in('location_id', uniqueIds);

  if (error) {
    return { managersByLocation: {}, error };
  }

  const managersByLocation: Record<string, LocationManager[]> = {};
  const rows = Array.isArray(data) ? data : [];
  for (const row of rows as any[]) {
    const locId = row?.location_id;
    const profile = row?.user_profiles;
    if (!locId || !profile || profile.is_archived || profile.is_banned) continue;
    const id = typeof profile.id === 'string' ? profile.id : null;
    if (!id) continue;
    const name = (typeof profile.full_name === 'string' && profile.full_name.trim()) || profile.email || 'Unnamed';
    const entry: LocationManager = { id, name, email: profile.email || null };
    if (!managersByLocation[locId]) {
      managersByLocation[locId] = [];
    }
    managersByLocation[locId].push(entry);
  }

  return { managersByLocation, error: null };
}

export async function refreshSessionContext(
  loadContext: (client: SupabaseClient) => Promise<void>,
  client?: SupabaseClient,
): Promise<void> {
  const supabase = client ?? getSupabaseClient();
  await loadContext(supabase);
}

export async function fetchActiveUserOptions(
  selectedId?: string | string[] | null,
  client?: SupabaseClient,
): Promise<{ data: UserOption[]; error: PostgrestError | null }> {
  const supabase = client ?? getSupabaseClient();
  const selectedIds = Array.isArray(selectedId)
    ? selectedId.map((id) => String(id || '').trim()).filter(Boolean)
    : selectedId
      ? [String(selectedId).trim()].filter(Boolean)
      : [];
  // Every page: past the API's row limit a single read dropped people silently (2026-10-22).
  const { data, error } = await readAllPages<ProfileNameRow>((from, to) => supabase
    .from('user_profiles')
    .select('id,full_name,first_name,last_name,email,is_archived')
    .neq('is_archived', true)
    .order('full_name', { ascending: true })
    .order('id', { ascending: true })
    .range(from, to));
  if (error) return { data: [], error };

  let options: UserOption[] = data.map((row) => ({ id: String(row.id), label: formatProfileDisplayName(row) }));

  const missingSelectedIds = selectedIds.filter((id) => !options.some((opt) => opt.id === id));
  if (missingSelectedIds.length) {
    const { data: fallbackRows } = await supabase
      .from('user_profiles')
      .select('id,full_name,first_name,last_name,email')
      .in('id', missingSelectedIds);
    const fallbackOptions = Array.isArray(fallbackRows)
      ? (fallbackRows as any[]).filter((row) => row?.id).map((row) => ({ id: String(row.id), label: formatProfileDisplayName(row) }))
      : [];
    options = [...options, ...fallbackOptions];
  }

  options.sort((a, b) => a.label.localeCompare(b.label));
  return { data: options, error: null };
}

// Service labor retains its role eligibility; general task assignments use fetchActiveUserOptions.
export async function fetchActiveServiceLaborUserOptions(
  client?: SupabaseClient,
): Promise<{ data: UserOption[]; error: PostgrestError | null }> {
  const supabase = client ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('service_labor_user_options');
  if (error) return { data: [], error };
  return { data: normalizeRpcUserOptions(data as ServiceLaborUserOptionRpcRow[]), error: null };
}

export async function fetchServiceTechnicianOptions(
  locationId: string | null | undefined,
  client?: SupabaseClient,
): Promise<{ data: UserOption[]; error: PostgrestError | null }> {
  if (!locationId) return { data: [], error: null };
  const supabase = client ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('service_technician_options', { p_location_id: locationId });
  // Never widen a failed or empty eligibility lookup to all user profiles.
  if (error) return { data: [], error };
  return { data: normalizeRpcUserOptions(data as ServiceLaborUserOptionRpcRow[]), error: null };
}

export async function fetchProfilesByAuthIds(
  authIds: string[],
  client?: SupabaseClient,
): Promise<{ data: UserAuthNameMap; headshots?: Record<string, string>; error: PostgrestError | null }> {
  if (!authIds.length) return { data: {}, headshots: {}, error: null };
  const supabase = client ?? getSupabaseClient();
  const uniqueIds = Array.from(new Set(authIds.filter(Boolean)));
  if (!uniqueIds.length) return { data: {}, headshots: {}, error: null };
  const { data, error } = await supabase
    .from('user_profiles')
    .select('auth_user_id,full_name,first_name,last_name,email,headshot_url')
    .in('auth_user_id', uniqueIds);
  if (error) return { data: {}, headshots: {}, error };
  const map: UserAuthNameMap = {};
  const headshots: Record<string, string> = {};
  for (const row of (data || []) as any[]) {
    const authId = row?.auth_user_id;
    if (!authId) continue;
    map[authId] = formatProfileDisplayName(row);
    if (typeof row.headshot_url === 'string' && row.headshot_url.trim()) {
      headshots[authId] = row.headshot_url.trim();
    }
  }
  return { data: map, headshots, error: null };
}

export async function fetchUserProfileOverview(params: {
  profileId: string;
  supabase?: SupabaseClient;
}): Promise<{ data: UserProfileOverview | null; error: PostgrestError | null }> {
  const { profileId } = params;
  if (!profileId) return { data: null, error: null };
  const supabase = params.supabase ?? getSupabaseClient();
  const [profileRes, assignedLocationsRes, allLocationsRes, assignedRegionsRes, allRegionsRes, noticePrefsRes] =
    await Promise.all([
      supabase.from('user_profiles').select(PROFILE_SELECT).eq('id', profileId).maybeSingle(),
      supabase
        .from('user_profile_locations')
        .select('location_id, locations(name)')
        .eq('user_profile_id', profileId)
        .order('assigned_at', { ascending: true }),
      supabase.from('locations').select('location_id, name').order('name', { ascending: true }),
      supabase
        .from('user_profile_regions')
        .select('region_id,is_manager,grant_all_locations, location_regions(name,is_active)')
        .eq('user_profile_id', profileId)
        .order('assigned_at', { ascending: true }),
      supabase.from('location_regions').select('region_id, name, is_active').order('is_active', { ascending: false }).order('name', { ascending: true }),
      supabase
        .from('user_notice_preferences')
        .select('notice_code, delivery_channels, reminder_days_before')
        .eq('user_profile_id', profileId),
    ]);

  const error =
    profileRes.error ||
    assignedLocationsRes.error ||
    allLocationsRes.error ||
    assignedRegionsRes.error ||
    allRegionsRes.error ||
    noticePrefsRes.error ||
    null;
  if (error) return { data: null, error };

  const profile = (profileRes.data as ProfileRow | null) ?? null;
  const assignedLocations: UserProfileLocation[] = Array.isArray(assignedLocationsRes.data)
    ? (assignedLocationsRes.data as any[]).map((row) => ({
        location_id: row.location_id,
        name: row.locations?.name || 'Unknown',
      }))
    : [];
  const allLocations: UserProfileLocation[] = Array.isArray(allLocationsRes.data)
    ? (allLocationsRes.data as any[]).map((row) => ({
        location_id: row.location_id,
        name: row.name,
      }))
    : [];
  const assignedRegions: UserProfileRegion[] = Array.isArray(assignedRegionsRes.data)
    ? (assignedRegionsRes.data as any[]).map((row) => ({
        region_id: row.region_id,
        name: row.location_regions?.name || 'Unknown region',
        is_active: row.location_regions?.is_active !== false,
        is_manager: !!row.is_manager,
        grant_all_locations: !!row.grant_all_locations,
      }))
    : [];
  const allRegions: UserProfileRegionOption[] = Array.isArray(allRegionsRes.data)
    ? (allRegionsRes.data as any[]).map((row) => ({
        region_id: row.region_id,
        name: row.name || 'Unnamed region',
        is_active: row.is_active !== false,
      }))
    : [];
  const prefRows = Array.isArray(noticePrefsRes.data) ? (noticePrefsRes.data as any[]) : [];
  const noticePrefs: Record<string, NoticeChannel[]> = {};
  for (const placeholder of NOTICE_PLACEHOLDERS) {
    const existing = prefRows.find((p: any) => p.notice_code === placeholder.code);
    if (existing && Array.isArray(existing.delivery_channels)) {
      noticePrefs[placeholder.code] = existing.delivery_channels.map((c: string) =>
        ['email', 'text', 'popup', 'none'].includes(c) ? (c as NoticeChannel) : 'email',
      );
    } else if (placeholder.defaultChannels?.length) {
      noticePrefs[placeholder.code] = [...placeholder.defaultChannels];
    } else {
      noticePrefs[placeholder.code] = ['email'];
    }
  }

  return {
    data: {
      profile,
      assignedLocations,
      allLocations,
      assignedRegions,
      allRegions,
      noticePrefs,
    },
    error: null,
  };
}

export const userProfileOverviewQueryKey = (profileId: string) => ['user-profile-overview', profileId];

export const getUserProfileOverviewQueryOptions = ({
  profileId,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  profileId: string;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: userProfileOverviewQueryKey(profileId),
  enabled: enabled && Boolean(profileId),
  queryFn: async () => {
    const { data, error } = await fetchUserProfileOverview({ profileId, supabase });
    if (error) throw error;
    return data;
  },
});
